import api from '@/services/api';
import type { RefLabel } from './productFees';

const smrPath = '/smr';

const paramsSerializer = {
    serialize: (params: Record<string, unknown>) => {
        const usp = new URLSearchParams();
        Object.entries(params).forEach(([key, value]) => {
            if (Array.isArray(value)) {
                value.forEach((v) => usp.append(`${key}[]`, String(v)));
            } else if (value !== undefined && value !== null) {
                usp.append(key, String(value));
            }
        });
        return usp.toString();
    },
};

export interface PaginationMeta {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
    from: number;
    to: number;
}

export function extractMeta(data: Record<string, unknown>): PaginationMeta {
    return {
        current_page: data.current_page as number,
        last_page: data.last_page as number,
        per_page: data.per_page as number,
        total: data.total as number,
        from: data.from as number,
        to: data.to as number,
    };
}

// ---- Services ----

export interface ServiceCostBreakdown {
    mode: 'total' | 'breakdown';
    // Absent in 'total' mode — a total-cost service has no line items to break down.
    items?: { net: number; gross: number };
    totals: { net: number; gross: number; vat_amount: number };
    vat_rate_percent: string; // e.g. "20.0000" -> "VAT (20%)"
}

export interface ServicePlanSummary {
    id: string;
    name: string;
}

export interface VatCategorySummary {
    id: string;
    code: string;
    name: string;
    rate_percent: string;
}

export type ServiceVatCategorySummary = VatCategorySummary;

export interface ServiceItemPivot {
    unit_quantity: number;
    unit_price_net: string;
}

export interface ServiceItemSummary {
    id: string;
    name?: string;
    pivot?: ServiceItemPivot;
}

export interface ServicePayloadInclusion {
    item_id: string;
    unit_quantity: number;
    unit_price_net: string;
}

export interface ServiceRecord {
    id: string;
    name: string;
    description?: string | null;
    status?: 'active' | 'retired';
    cost_mode?: 'total' | 'breakdown';
    vat_category_id?: string | null;
    vat_category?: ServiceVatCategorySummary | null;
    total_cost_net?: string | null;
    total_cost_gross?: string | null;
    price?: number;
    gross_price?: number;
    cost_breakdown?: ServiceCostBreakdown;
    // Present on GET /services/{id} and the plan services rows; seeds the form's inclusion rows.
    items?: ServiceItemSummary[];
    plan?: ServicePlanSummary | null;
    position?: number;
    service_type?: string;
    months_from_start?: number;
    mileage_from_start?: number;
    created_at: string;
    updated_at: string;
}

export interface ServicePayload {
    name: string;
    description?: string | null;
    status?: 'active' | 'retired';
    cost_mode: 'total' | 'breakdown';
    vat_category_id: string;
    total_cost_net?: string | null;
    total_cost_gross?: string | null;
    position?: number;
    service_type?: string;
    months_from_start?: number;
    mileage_from_start?: number;
    confirm_inclusions_deletion?: boolean;
    items?: ServicePayloadInclusion[];
}

export interface ServiceListParams {
    search?: string;
    status?: 'active';
    page?: number;
    per_page?: number;
    sort_by?: string;
    sort_dir?: 'asc' | 'desc';
}

export interface ServicesPage {
    data: ServiceRecord[];
    meta: PaginationMeta;
}

export const fetchServices = async (params: ServiceListParams = {}): Promise<ServicesPage> => {
    const response = await api.get(`${smrPath}/services`, { params });
    const raw = response.data as Record<string, unknown>;
    const items = ((raw.data ?? []) as ServiceRecord[]);
    return { data: items, meta: extractMeta(raw) };
};

export const fetchService = async (id: string): Promise<ServiceRecord> => {
    const response = await api.get(`${smrPath}/services/${id}`);
    return (response.data.data ?? response.data) as ServiceRecord;
};

export const updateService = async (id: string, payload: ServicePayload): Promise<ServiceRecord> =>
    ((await api.put(`${smrPath}/services/${id}`, payload)).data.data) as ServiceRecord;

export const deleteService = async (id: string): Promise<void> => {
    await api.delete(`${smrPath}/services/${id}`);
};

export const retireService = async (id: string): Promise<ServiceRecord> =>
    ((await api.patch(`${smrPath}/services/${id}/retire`)).data.data) as ServiceRecord;

// ---- Items (stub — Phase 3 will extend) ----

export interface ItemRecord {
    id: string;
    name: string;
    code?: string | null;
    description?: string | null;
    created_at: string;
    updated_at: string;
}

export interface ItemPayload {
    name: string;
    code?: string | null;
    description?: string | null;
}

export interface SimpleListParams {
    page?: number;
    per_page?: number;
}

export interface ItemListParams extends SimpleListParams {
    search?: string;
}

export interface ItemsPage {
    data: ItemRecord[];
    meta: PaginationMeta;
}

export const fetchItems = async (
    params: ItemListParams = {},
    opts?: { suppressForbiddenToast?: boolean },
): Promise<ItemsPage> => {
    const { search, ...rest } = params;
    const response = await api.get(`${smrPath}/items`, {
        params: { ...rest, search: search?.trim() || undefined },
        suppressGlobalForbiddenToast: opts?.suppressForbiddenToast,
    });
    const raw = response.data as Record<string, unknown>;
    return { data: (raw.data ?? []) as ItemRecord[], meta: extractMeta(raw) };
};

export const fetchItem = async (id: string): Promise<ItemRecord> => {
    const response = await api.get(`${smrPath}/items/${id}`);
    return (response.data.data ?? response.data) as ItemRecord;
};

export const createItem = async (payload: ItemPayload): Promise<ItemRecord> =>
    ((await api.post(`${smrPath}/items`, payload)).data.data) as ItemRecord;

export const updateItem = async (id: string, payload: ItemPayload): Promise<ItemRecord> =>
    ((await api.put(`${smrPath}/items/${id}`, payload)).data.data) as ItemRecord;

export const deleteItem = async (id: string): Promise<void> => {
    await api.delete(`${smrPath}/items/${id}`);
};

export interface ItemNameSummary {
    id: string;
    name: string;
    code?: string | null;
    description?: string | null;
}

export const fetchItemsByIds = async (ids: string[]): Promise<ItemNameSummary[]> => {
    const unique = Array.from(new Set(ids.filter(Boolean))).sort();
    if (unique.length === 0) return [];
    const response = await api.get<{ data: ItemNameSummary[] }>(`${smrPath}/items/by-ids`, {
        params: { ids: unique },
        paramsSerializer,
    });
    return response.data.data;
};

// ---- Fees ----

export interface FeeRecord {
    id: string;
    name: string;
    transaction_type_id: string;
    transaction_type?: (RefLabel & { numeric_ref: number | null }) | null; // display only
    created_at: string;
    updated_at: string;
}

export interface FeePayload {
    name: string;
    transaction_type_id: string;
}

export interface FeesPage {
    data: FeeRecord[];
    meta: PaginationMeta;
}

export const fetchFees = async (params: SimpleListParams = {}): Promise<FeesPage> => {
    const response = await api.get(`${smrPath}/fees`, { params });
    const raw = response.data as Record<string, unknown>;
    return { data: (raw.data ?? []) as FeeRecord[], meta: extractMeta(raw) };
};

export const fetchFee = async (id: string): Promise<FeeRecord> => {
    const response = await api.get(`${smrPath}/fees/${id}`);
    return (response.data.data ?? response.data) as FeeRecord;
};

export const createFee = async (payload: FeePayload): Promise<FeeRecord> =>
    ((await api.post(`${smrPath}/fees`, payload)).data.data) as FeeRecord;

export const updateFee = async (id: string, payload: FeePayload): Promise<FeeRecord> =>
    ((await api.put(`${smrPath}/fees/${id}`, payload)).data.data) as FeeRecord;

export const deleteFee = async (id: string): Promise<void> => {
    await api.delete(`${smrPath}/fees/${id}`);
};

// ---- Config Options ----

export type ConfigOptionDataType = 'string' | 'integer' | 'decimal' | 'boolean' | 'money' | 'multiselect';

export interface ConfigOptionRecord {
    id: string;
    key_name: string;
    label?: string | null;
    data_type: ConfigOptionDataType;
    description?: string | null;
    group?: 'financial' | 'coverage' | 'policies' | 'general' | 'money_handling' | 'payment_method' | null;
    validation_rules?: Record<string, unknown> | null;
    is_required: boolean;
    // Cardinality of the KEY, not of the value (#735). false = at most one row per
    // owner is meaningful for this key; true = many rows are legitimate (today:
    // `features` only). NOT optional: the column is NOT NULL and the API always
    // emits it, so every fixture constructing a ConfigOptionRecord must set it.
    is_multi_valued: boolean;
    settings_count?: number;
    unlinked_count?: number;
    created_at: string;
    updated_at: string;
}

export interface BulkLinkResult {
    linked_count: number;
    setting_ids: string[];
}

export const bulkLinkConfigOption = async (
    id: string,
    opts: { force?: boolean } = {},
): Promise<BulkLinkResult> => {
    const body = opts.force ? { force: true } : {};
    const response = await api.post(`${smrPath}/config-options/${id}/bulk-link`, body);
    return (response.data.data ?? response.data) as BulkLinkResult;
};

export interface ConfigOptionListParams {
    page?: number;
    per_page?: number;
    group?: 'financial' | 'coverage' | 'policies' | 'general' | 'money_handling' | 'payment_method';
    search?: string;
}

// Catalogue groups that belong to the CAMPAIGN side only (Cancellation `policies`,
// Banner `general`, CTHF `money_handling`). They live in the shared global
// `product_config_options` catalogue so the campaign tabs can reuse the picker, but
// they must never surface in the PRODUCT-side pickers (`ConfigSettingAddForm`
// combobox and the product `ApplyCatalogModal` "Any group" path), which would
// otherwise let an admin apply a campaign-only capability as a product config
// setting, where nothing reads it. Product groups (`financial`, `coverage`) stay
// available on both sides.
//
// `payment_method` is DELIBERATELY NOT here (D5/D10 —
// docs/roadmap/3 - Agreements Slice 2 - Product Config Authority.md §5): it is the
// exact opposite, PRODUCT-only. `partner_handled` moved from Campaign to Product
// config settings under #712's re-purposing; the campaign side now REJECTS it
// server-side (CampaignConfigSettingService::assertNotProductOnly(), a 422), and this
// constant is what lets the product-side pickers offer it.
export const CAMPAIGN_ONLY_CONFIG_GROUPS = ['policies', 'general', 'money_handling'] as const;

export const isCampaignOnlyConfigGroup = (group?: string | null): boolean =>
    group != null && (CAMPAIGN_ONLY_CONFIG_GROUPS as readonly string[]).includes(group);

export interface ConfigOptionsPage {
    data: ConfigOptionRecord[];
    meta: PaginationMeta;
}

export const fetchConfigOptions = async (params: ConfigOptionListParams = {}): Promise<ConfigOptionsPage> => {
    const response = await api.get(`${smrPath}/config-options`, { params });
    const raw = response.data as Record<string, unknown>;
    return { data: (raw.data ?? []) as ConfigOptionRecord[], meta: extractMeta(raw) };
};

export const fetchConfigOption = async (id: string): Promise<ConfigOptionRecord> => {
    const response = await api.get(`${smrPath}/config-options/${id}`);
    return (response.data.data ?? response.data) as ConfigOptionRecord;
};

// ---- Add-ons ----

export interface MoneyShape { amount: string; currency: string; minor: number; }

export interface AddOnRecord {
    id: string;
    name: string;
    description?: string | null;
    category?: string | null;
    vat_category_id?: string;
    vat_category?: VatCategorySummary | null;
    price: string;
    gross_price: string;
    price_money?: MoneyShape | null;
    gross_price_money?: MoneyShape | null;
    vat_amount?: string | null;
    vat_amount_money?: MoneyShape | null;
    vat_rate_percent?: string | null;
    retired_at?: string | null;
    created_at: string;
    updated_at: string;
}

export interface AddOnPayload {
    name: string;
    description?: string | null;
    category?: string | null;
    price: string;
    vat_category_id: string;
}

export interface AddOnListParams {
    search?: string;
    status?: 'active';
    category?: string;
    page?: number;
    per_page?: number;
    sort_by?: 'name' | 'category' | 'price' | 'gross_price' | 'retired_at' | 'created_at';
    sort_dir?: 'asc' | 'desc';
}

export interface AddOnsPage { data: AddOnRecord[]; meta: PaginationMeta; }

export const fetchAddOns = async (params: AddOnListParams = {}): Promise<AddOnsPage> => {
    const response = await api.get(`${smrPath}/addons`, { params });
    const raw = response.data as Record<string, unknown>;
    return { data: (raw.data ?? []) as AddOnRecord[], meta: extractMeta(raw) };
};

export const fetchAddOn = async (id: string): Promise<AddOnRecord> =>
    ((await api.get(`${smrPath}/addons/${id}`)).data.data) as AddOnRecord;

export const createAddOn = async (payload: AddOnPayload): Promise<AddOnRecord> =>
    ((await api.post(`${smrPath}/addons`, payload)).data.data) as AddOnRecord;

export const updateAddOn = async (id: string, payload: Partial<AddOnPayload>): Promise<AddOnRecord> =>
    ((await api.put(`${smrPath}/addons/${id}`, payload)).data.data) as AddOnRecord;

export const retireAddOn = async (id: string): Promise<AddOnRecord> =>
    ((await api.patch(`${smrPath}/addons/${id}/retire`)).data.data) as AddOnRecord;
